import React, { useEffect, useState } from 'react'
import { Card, CardBody, CardHeader } from '../../../../_metronic/_partials/controls'
import {
  Button,
  TextField,
  FormControlLabel,
  Checkbox,
  MuiThemeProvider,
  createMuiTheme,
} from '@material-ui/core'
import { useHistory, useParams } from 'react-router-dom'
import MultilanguageTabBlock from '../../../components/MultilanguageTabBlock'
import ConfirmDialog from '../../../components/dialogs/ConfirmDialog'
import {
  getContactAdmin,
  addContactEntry,
  updateContactEntry,
  deleteContactEntry,
} from '../../../../api/contact'
import { useSkeleton } from '../../../hooks/useSkeleton'
import { alertError, alertSuccess } from '../../../../utils/logger'

const theme = createMuiTheme({
  palette: { secondary: { main: '#F64E60' } },
})

function getEmpty() {
  return {
    title: {},
    description: {},
    phone: '',
    email: '',
    active: true,
  }
}

function hasLocalizedText(map) {
  if (!map || typeof map !== 'object') return false
  return Object.values(map).some((v) => v != null && String(v).trim().length > 0)
}

export default function EditContactPage() {
  const [entry, setEntry] = useState(getEmpty())
  const [openConfirmDialog, setOpenConfirmDialog] = useState(false)
  const [nextOrder, setNextOrder] = useState(0)
  const entryId = useParams().id
  const history = useHistory()
  const { isLoading: isLoadingData, disableLoading: disableLoadingData, ContentSkeleton } =
    useSkeleton()

  useEffect(() => {
    getContactAdmin()
      .then((res) => {
        if (res.status !== 200) return
        const entries = res.data.entries || []
        if (!entryId) {
          const maxOrder = entries.reduce((max, e) => Math.max(max, Number(e.order) || 0), -1)
          setNextOrder(maxOrder + 1)
          disableLoadingData()
          return
        }
        const found = entries.find((e) => e._id === entryId)
        if (!found) {
          history.push('/contact')
          return
        }
        setEntry({
          title: found.title && typeof found.title === 'object' ? found.title : {},
          description:
            found.description && typeof found.description === 'object'
              ? found.description
              : {},
          phone: found.phone || '',
          email: found.email || '',
          active: found.active !== false,
        })
        disableLoadingData()
      })
      .catch((error) => {
        alertError({ error, customMessage: 'No se pudo cargar la entrada de contacto.' })
        history.push('/contact')
      })
  }, [entryId, disableLoadingData, history])

  function updateLocalized(field, lang, value) {
    setEntry((prev) => ({
      ...prev,
      [field]: { ...(prev[field] || {}), [lang]: value },
    }))
  }

  function save() {
    if (!hasLocalizedText(entry.title) || !hasLocalizedText(entry.description)) {
      alertError({
        error: null,
        customMessage: 'Título y descripción son obligatorios (al menos un idioma).',
      })
      return
    }
    if (!(entry.phone || '').trim() && !(entry.email || '').trim()) {
      alertError({
        error: null,
        customMessage: 'Indica teléfono o correo.',
      })
      return
    }

    const payload = {
      title: entry.title || {},
      description: entry.description || {},
      phone: (entry.phone || '').trim(),
      email: (entry.email || '').trim(),
      active: entry.active !== false,
    }
    if (!entryId) {
      payload.order = nextOrder
    }

    const req = entryId ? updateContactEntry(entryId, payload) : addContactEntry(payload)
    req
      .then((res) => {
        if (res.status === 200 || res.status === 201) {
          alertSuccess({ title: 'Guardado', customMessage: 'Entrada de contacto guardada.' })
          history.push('/contact')
        }
      })
      .catch((error) => {
        alertError({ error, customMessage: 'No se pudo guardar la entrada.' })
      })
  }

  if (isLoadingData) return <ContentSkeleton />

  return (
    <>
      <Card>
        <CardHeader title={entryId ? 'Editar contacto' : 'Nueva entrada de contacto'} />
        <CardBody>
          <MultilanguageTabBlock
            multilanguageTabContent={(lang) => (
              <>
                <TextField
                  label="Título"
                  value={(entry.title && entry.title[lang]) || ''}
                  onChange={(e) => updateLocalized('title', lang, e.target.value)}
                  margin="normal"
                  variant="outlined"
                  fullWidth
                  required
                  InputLabelProps={{ shrink: true }}
                />
                <TextField
                  label="Descripción"
                  value={(entry.description && entry.description[lang]) || ''}
                  onChange={(e) => updateLocalized('description', lang, e.target.value)}
                  margin="normal"
                  variant="outlined"
                  fullWidth
                  required
                  multiline
                  rows={3}
                  InputLabelProps={{ shrink: true }}
                />
              </>
            )}
          />
          <TextField
            label="Teléfono"
            value={entry.phone || ''}
            onChange={(e) => setEntry({ ...entry, phone: e.target.value })}
            margin="normal"
            variant="outlined"
            style={{ marginRight: 16 }}
            InputLabelProps={{ shrink: true }}
          />
          <TextField
            label="Correo"
            value={entry.email || ''}
            onChange={(e) => setEntry({ ...entry, email: e.target.value })}
            margin="normal"
            variant="outlined"
            InputLabelProps={{ shrink: true }}
          />
          <br />
          <FormControlLabel
            control={
              <Checkbox
                checked={entry.active !== false}
                onChange={() => setEntry({ ...entry, active: !(entry.active !== false) })}
              />
            }
            label="Activo"
          />
        </CardBody>
      </Card>
      <Button
        onClick={() => history.push('/contact')}
        variant="outlined"
        style={{ marginRight: 20 }}
      >
        Volver
      </Button>
      <Button onClick={save} variant="outlined" color="primary" style={{ marginRight: 20 }}>
        Guardar
      </Button>
      {entryId && (
        <>
          <MuiThemeProvider theme={theme}>
            <Button onClick={() => setOpenConfirmDialog(true)} variant="outlined" color="secondary">
              Eliminar
            </Button>
          </MuiThemeProvider>
          <ConfirmDialog
            title="¿Eliminar esta entrada de contacto?"
            open={openConfirmDialog}
            setOpen={setOpenConfirmDialog}
            onConfirm={() => {
              deleteContactEntry(entryId)
                .then((res) => {
                  if (res.status === 200 || res.status === 204) {
                    alertSuccess({ title: 'Eliminado', customMessage: 'Entrada eliminada.' })
                    history.push('/contact')
                  }
                })
                .catch((error) => {
                  alertError({ error, customMessage: 'No se pudo eliminar la entrada.' })
                })
            }}
          />
        </>
      )}
    </>
  )
}
